import { useMemo } from "react";
import { AccessLevel, Action, RoleType } from "@/shared/constants/roles";
import { ResourceEnum } from "@/shared/constants/resources";
import { PersonRole } from "@/features/auth/graphql/generated";
import { useViewer } from "@/features/profile/hooks/useViewer";
import { useOrganisation } from "@/features/organization/hooks/useOrganisation";
import { useAccount } from "./useAccount";
import { getPersonRoleObject } from "@/shared/utils/selectors/getRolesAndAccess";

interface ValidatePermissionsParams {
  resourceType: ResourceEnum;
  resourceId?: number;
  accessLevel: AccessLevel;
}

interface ValidateResourceActionParams {
  resourceType: ResourceEnum;
  resourceId: number;
  action: Action;
}

const getAccessLevelOrderForResource = (
  resourceType: ResourceEnum
): AccessLevel[] => {
  switch (resourceType) {
    case ResourceEnum.account:
    case ResourceEnum.organization:
      return [
        AccessLevel.none,
        AccessLevel.limited,
        AccessLevel.edit,
        AccessLevel.full,
      ];
    case ResourceEnum.team:
    case ResourceEnum.project:
      return [AccessLevel.none, AccessLevel.read, AccessLevel.full];
    default:
      return [AccessLevel.none, AccessLevel.full];
  }
};

/**
 * useViewerPermissions Hook
 *
 * Implements the hierarchical permissions system as documented in PERMISSIONS_SYSTEM.md
 */
const useViewerPermissions = () => {
  const { viewer } = useViewer();
  const { organization } = useOrganisation();
  const { account } = useAccount();

  const roles = useMemo(
    () =>
      (viewer.person?.roles || []).filter(
        (r: unknown): r is PersonRole => r != null
      ),
    [viewer.person?.roles]
  );

  /**
   * Find role by resource type and id
   */
  const findRole = (resourceType: ResourceEnum, resourceId: number) => {
    return getPersonRoleObject(roles, resourceType, resourceId);
  };

  /**
   * Compare access levels
   */
  const hasAccessLevel = (
    resourceType: ResourceEnum,
    userLevel: string | null | undefined,
    requiredLevel: AccessLevel
  ): boolean => {
    if (!userLevel) return false;
    const levels = getAccessLevelOrderForResource(resourceType);
    const userIdx = levels.indexOf(userLevel as AccessLevel);
    const requiredIdx = levels.indexOf(requiredLevel);

    // Unknown/invalid level for this resource type => deny
    if (userIdx === -1 || requiredIdx === -1) return false;

    // Check if user has hierarchy access to the resource
    return userIdx >= requiredIdx;
  };

  /**
   * Validate access to a resource
   *
   * @param params.resourceType - Type of resource (ACCOUNT, ORGANIZATION, TEAM, PROJECT)
   * @param params.resourceId - ID of the resource (optional for ACCOUNT/ORGANIZATION using context)
   * @param params.action - Action to perform (VIEW, MODIFY, DELETE)
   * @returns boolean indicating if user has required permission
   */
  const validateAccessToResource = ({
    resourceType,
    resourceId,
    accessLevel,
  }: ValidatePermissionsParams): boolean => {
    if (!viewer.person || !account.id) return false;

    // Get account role
    const accountRole = findRole(ResourceEnum.account, account.id);

    // Account OWNER has access to everything
    if (accountRole?.role === RoleType.owner) return true;

    // Account ADMIN with access to sub-resources based on their access level
    if (accountRole?.role === RoleType.admin) {
      // For account-level actions, check if ADMIN has sufficient access
      if (resourceType === ResourceEnum.account) {
        return hasAccessLevel(
          ResourceEnum.account,
          accountRole.access_level,
          accessLevel
        );
      }

      // ADMIN with FULL has access to ALL organizations
      if (
        hasAccessLevel(
          ResourceEnum.account,
          accountRole.access_level,
          AccessLevel.full
        )
      ) {
        // PERMISSIONS_SYSTEM.md: Workspace ADMIN with FULL has FULL access in all sub-resources.
        // Only handle sub-resources here (account already handled above).
        if (
          resourceType === ResourceEnum.organization ||
          resourceType === ResourceEnum.team ||
          resourceType === ResourceEnum.project
        ) {
          return hasAccessLevel(resourceType, AccessLevel.full, accessLevel);
        }
      }
    }

    const _resourceId =
      resourceId ??
      (resourceType === ResourceEnum.account
        ? account.id
        : resourceType === ResourceEnum.organization
        ? organization.id
        : undefined);

    // Team/Project require explicit resourceId
    if (
      (resourceType === ResourceEnum.team ||
        resourceType === ResourceEnum.project) &&
      !_resourceId
    ) {
      return false;
    }
    if (!_resourceId) return false;

    // Organization FULL (selected org context) implies FULL access in all sub-resources (teams/projects)
    // within that selected org context (PERMISSIONS_SYSTEM.md: Organization ADMIN is always FULL).

    if (organization.id) {
      const organizationRole = findRole(
        ResourceEnum.organization,
        organization.id
      );

      const hasOrgFullAccess = hasAccessLevel(
        ResourceEnum.organization,
        organizationRole?.access_level,
        AccessLevel.full
      );

      // Only apply org inheritance within the selected org context:
      // - If checking the org itself, ensure it's the selected org.
      // - If checking a team/project, we assume the current UI context is already scoped to the selected org.
      if (
        hasOrgFullAccess &&
        ((resourceType === ResourceEnum.organization &&
          _resourceId === organization.id) ||
          resourceType === ResourceEnum.team ||
          resourceType === ResourceEnum.project)
      ) {
        return hasAccessLevel(resourceType, AccessLevel.full, accessLevel);
      }
    }

    // Check specific resource role and access level
    const resourceRole = findRole(resourceType, _resourceId);

    if (!resourceRole) return false;

    return hasAccessLevel(resourceType, resourceRole.access_level, accessLevel);
  };

  /**
   * Validate action on a resource. This is a more specific validation than validateAccessToResource,
   * mostly handles modify and delete actions and other specific cases.
   *
   * @param params.resourceType - Type of resource (ACCOUNT, ORGANIZATION, TEAM, PROJECT)
   * @param params.resourceId - ID of the resource
   * @param params.action - Action to perform (VIEW, MODIFY, DELETE)
   * @returns boolean indicating if user has required permission
   */
  const validateResourceAction = ({
    resourceType,
    resourceId,
    action,
  }: ValidateResourceActionParams): boolean => {
    const accountRole = findRole(ResourceEnum.account, account.id!);
    const organizationRole = findRole(
      ResourceEnum.organization,
      organization.id!
    );
    // Account actions
    if (resourceType === ResourceEnum.account) {
      // Only OWNER can delete an account
      if (action === Action.delete) {
        return accountRole?.role === RoleType.owner;
      }

      if (action === Action.modify) {
        return (
          accountRole?.role === RoleType.owner ||
          hasAccessLevel(
            ResourceEnum.account,
            accountRole?.access_level,
            AccessLevel.full
          )
        );
      }
    }

    // Organization actions
    if (resourceType === ResourceEnum.organization) {
      if (action === Action.delete) {
        return (
          accountRole?.role === RoleType.owner ||
          hasAccessLevel(
            ResourceEnum.organization,
            organizationRole?.access_level,
            AccessLevel.full
          )
        );
      }

      if (action === Action.modify) {
        return (
          accountRole?.role === RoleType.owner ||
          hasAccessLevel(
            ResourceEnum.organization,
            organizationRole?.access_level,
            AccessLevel.full
          )
        );
      }
    }

    return false;
  };

  return {
    viewer,
    validateAccessToResource,
    validateResourceAction,
    // Important: viewer.person may exist before roles have been fetched.
    // Treat "roles not yet loaded" as loading, otherwise permission checks will
    // briefly deny and show NoRights before the spinner/content.
    permissionsLoading:
      viewer.person?.roles == null || !account.id || !organization.id,
  };
};

export default useViewerPermissions;
